import { MethodMarks } from '../method-marks';
import { MarkedProxyDecorator } from '../proxy.decorator';

export interface IMetrics {
  addCountMetric(name: string, value: number): void;
}

export interface CallMeterOptions {
  /** Base the metric names are built from: `${name}Succeeded` / `${name}Failed`. */
  name: string;
}

/**
 * Per-method overrides, applied on top of the instance {@link CallMeterOptions}.
 *
 * Derived with `Pick` so it cannot drift from the options it refines.
 */
export type MeterOverrides = Pick<CallMeterOptions, 'name'>;

const marks = new MethodMarks<MeterOverrides>('shared-decorators:meteredMethods');

/**
 * Opts a method into {@link CallMeter}, and optionally names its metrics.
 *
 * A gate, like {@link retryable} and {@link rateLimited}: an unmarked method emits
 * nothing. Bare, it reports under the instance's base name — which is what you want when
 * the counters are already split by dimension. Name it only when a method has to be
 * distinguishable on its own, since otherwise every method on a target reports the same
 * two counters and a spike tells you something failed but not what.
 *
 * @example
 * class S3FileRepository {
 *   @metered({ name: 'AssetFetch' })
 *   public fetch(path: string): Promise<Buffer> { ... }   // AssetFetchSucceeded / AssetFetchFailed
 *
 *   @metered()
 *   public async list(path: string): Promise<string[]> { ... }   // OperationSucceeded / OperationFailed
 *
 *   public getAbsolutePath(name: string): string { ... }   // never metered
 * }
 */
export function metered(overrides?: MeterOverrides): MethodDecorator {
  return (prototype, propertyKey) => marks.add(prototype, propertyKey, overrides);
}

/**
 * The metric-name overrides declared on `target` or up its prototype chain.
 *
 * A delegating function rather than `= marks.of`: detaching a method from its instance
 * loses `this`, which would compile and then fail at runtime.
 */
export function meteredMethodsOf(target: object): ReadonlyMap<string, MeterOverrides | undefined> {
  return marks.of(target);
}

const DEFAULT_METRIC_NAME = 'Operation';

/**
 * CallMeter
 *
 * A proxy-based decorator that records metrics for method execution:
 * - Records an "Error" metric (count 1) when a method throws or rejects.
 * - Records a "Success" metric (count 1) when a method completes without throwing.
 *
 * Metric names come from the instance's base name, or from a {@link metered} mark when a
 * method needs to be distinguishable on its own. Unmarked methods all report the same two
 * counters, which is fine for a single-purpose handler and unhelpful for an adapter.
 *
 * **Compose it outside a {@link Retrier}**, so a call that failed twice and then succeeded
 * counts as one success. Inside, every recovered attempt would also report a failure and
 * the metric would measure retry pressure rather than outcomes. Put {@link CallLogger}
 * on the other side — inside the retrier — where per-attempt detail is what you want.
 *
 * Dimensions are not set here; set them in the caller (e.g. in the entrypoint index) so all metrics for the invocation share the same dimensions.
 *
 * Buffered metrics must be flushed before the invocation ends — call metrics.publishStoredMetrics() in the handler.
 *
 * @example
 * const metering = new CallMeter(metrics);
 * const measuredHandler = metering.decorate(logicHandler);   // OperationSucceeded / OperationFailed
 */
export class CallMeter extends MarkedProxyDecorator<MeterOverrides> {
  constructor(
    private readonly metrics: IMetrics,
    private readonly options: CallMeterOptions = { name: DEFAULT_METRIC_NAME }
  ) {
    super(marks);
  }

  protected wrapMarked(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    override: MeterOverrides | undefined
  ): (...args: unknown[]) => unknown {
    const name = override?.name ?? this.options.name;

    return (...args: unknown[]) => {
      try {
        const result = method(...args);

        if (result instanceof Promise) {
          return result
            .then((value) => {
              this.record(`${name}Succeeded`);
              return value;
            })
            .catch((err: unknown) => {
              this.record(`${name}Failed`);
              throw err;
            });
        }

        this.record(`${name}Succeeded`);
        return result;
      } catch (err) {
        this.record(`${name}Failed`);
        throw err;
      }
    };
  }

  private record(metric: string) {
    this.metrics.addCountMetric(metric, 1);
  }
}
